import { Box, Checkbox, CircularProgress, Typography } from "@mui/material";
import { Colors } from "../../Colors";
import SettingsLayout from "./SettingsLayout";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { useEffect, useState } from "react";
import { PrimaryButton } from "../../subcomponents/CustomButton";
import { updateAgentThunk } from "../../slices/AgentSlice";
import { updateOrganizationAttribute } from "../../slices/AuthSlice";
import { Agent } from "../../types";
import { useSnackbar } from "../../providers/SnackbarProvider";
import SettingRow from "./SettingRow";

export default function AutoAddSettings() {
  const dispatch = useAppDispatch();
  const { showMessage } = useSnackbar();

  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.auth.organization);

  const agents = useSelector((state: RootState) => state.agents.agents);
  const agent = agents && agents.length > 0 ? agents[0] : null;

  const [isLoading, setIsLoading] = useState(false);

  const [autoAddEnabled, setAutoAddEnabled] = useState(organization?.autoAddEnabled || false);
  const [autoAddMultiCaseStudy, setAutoAddMultiCaseStudy] = useState(organization?.autoAddMultiCaseStudy || false);
  const [autoDischargeEnabled, setAutoDischargeEnabled] = useState(organization?.autoDischargeEnabled || false);
  const [skipDischargeIfActiveCaseExists, setSkipDischargeIfActiveCaseExists] = useState(organization?.skipDischargeIfActiveCaseExists || false);
  const [deleteFutureAppointmentsOnDischarge, setDeleteFutureAppointmentsOnDischarge] = useState(agent?.deleteFutureAppointmentsOnDischarge || false);

  useEffect(() => {
    if (organization) {
      setAutoAddEnabled(organization.autoAddEnabled || false);
      setAutoAddMultiCaseStudy(organization.autoAddMultiCaseStudy || false);
      setAutoDischargeEnabled(organization.autoDischargeEnabled || false);
      setSkipDischargeIfActiveCaseExists(organization.skipDischargeIfActiveCaseExists || false);
    }
  }, [organization]);

  useEffect(() => {
    if (agent) {
      setDeleteFutureAppointmentsOnDischarge(agent.deleteFutureAppointmentsOnDischarge || false);
    }
  }, [agent]);

  const handleSave = async () => {
    if (!user?.token || !organization) {
      showMessage("User session or organization not found.", "error");
      return;
    }

    setIsLoading(true);
    try {
      if (autoAddEnabled !== (organization.autoAddEnabled || false)) {
        await dispatch(updateOrganizationAttribute({ token: user.token, attribute: "autoAddEnabled", value: autoAddEnabled })).unwrap();
      }

      if (autoAddMultiCaseStudy !== (organization.autoAddMultiCaseStudy || false)) {
        await dispatch(updateOrganizationAttribute({ token: user.token, attribute: "autoAddMultiCaseStudy", value: autoAddMultiCaseStudy })).unwrap();
      }

      if (autoDischargeEnabled !== (organization.autoDischargeEnabled || false)) {
        await dispatch(updateOrganizationAttribute({ token: user.token, attribute: "autoDischargeEnabled", value: autoDischargeEnabled })).unwrap();
      }

      if (skipDischargeIfActiveCaseExists !== (organization.skipDischargeIfActiveCaseExists || false)) {
        await dispatch(updateOrganizationAttribute({ token: user.token, attribute: "skipDischargeIfActiveCaseExists", value: skipDischargeIfActiveCaseExists })).unwrap();
      }

      if (agent && deleteFutureAppointmentsOnDischarge !== (agent.deleteFutureAppointmentsOnDischarge || false)) {
        const agentProps: Partial<Agent> = {
          agentId: agent.agentId,
          deleteFutureAppointmentsOnDischarge,
        };
        await dispatch(updateAgentThunk({ token: user.token, agent: agentProps })).unwrap();
      }

      showMessage("Automation settings updated successfully.", "success");
    } catch (error) {
      console.error("Failed to update automation settings:", error);
      showMessage("Failed to update automation settings.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SettingsLayout>
      <Box pb={6}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Automation Settings
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="h6" sx={{ mt: 1 }}>
            Auto Add Patients
          </Typography>
          <SettingRow
            label="Enable Auto Add Patients"
            description={
              <>
                Automatically adds any patients that appear in the Recommended Patients to Add page to Penciled. For more information, check{" "}
                <a href="https://www.penciled.com/docs/product/add-patient/auto-add" target="_blank" rel="noopener noreferrer" style={{ color: Colors.blue1, textDecoration: "underline" }}>
                  the docs
                </a>
                .
              </>
            }
          >
            <Checkbox checked={autoAddEnabled} onChange={(e) => setAutoAddEnabled(e.target.checked)} />
          </SettingRow>
          <SettingRow
            label="Auto add all case studies (Affects Quick Add too)"
            description="When enabled, patients who have more than one active case study will have all their case studies added. By default, only the most recent active case study is added for each patient."
          >
            <Checkbox checked={autoAddMultiCaseStudy} onChange={(e) => setAutoAddMultiCaseStudy(e.target.checked)} />
          </SettingRow>

          <Typography variant="h6" sx={{ mt: 4 }}>
            Auto Archive Patients
          </Typography>
          <SettingRow label="Enable Auto Archive Patients" description="Automatically archives any patients whose case study has been marked as discharged in WebPT.">
            <Checkbox checked={autoDischargeEnabled} onChange={(e) => setAutoDischargeEnabled(e.target.checked)} />
          </SettingRow>
          <SettingRow
            label="Skip discharge if active case exists on WebPT"
            description="Usually a patient is archived when all their active cases that have been added to Penciled are discharged. Enabling this will only archive patients when all their cases on WebPT are discharged, even if some of those cases haven't been added to Penciled yet."
          >
            <Checkbox checked={skipDischargeIfActiveCaseExists} onChange={(e) => setSkipDischargeIfActiveCaseExists(e.target.checked)} />
          </SettingRow>
          <SettingRow
            label="Delete future appointments when a patient is discharged"
            description="When a patient is discharged, all of their future scheduled appointments will be automatically deleted."
          >
            <Checkbox checked={deleteFutureAppointmentsOnDischarge} onChange={(e) => setDeleteFutureAppointmentsOnDischarge(e.target.checked)} />
          </SettingRow>
        </Box>
        <Box mt={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PrimaryButton onClick={handleSave} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
          </PrimaryButton>
        </Box>
      </Box>
    </SettingsLayout>
  );
}
